/**
 * Sunucular — the server fleet, as a live master/detail command view.
 *
 * The load-bearing part of this screen is still the host-key alarm. Kimene never
 * auto-accepts a changed SSH host key, and an interface that mentioned that in a
 * tooltip would be lying about how serious it is — a changed key is either a
 * rebuilt machine or someone standing between you and it, and the console cannot
 * tell which. So the alarm is a full-width band above everything, the affected
 * host's actions are disabled at source, and both fingerprints are shown in full.
 * That verdict comes from a connection attempt (POST /hosts/{id}/test), not from
 * the list, and is held for the life of the screen.
 *
 * Everything else is a reading of one live document: GET /system/overview, the
 * only source of host utilisation (the /ws/events socket's host metrics are
 * discarded — see fleet-telemetry). The signature is the vitals fingerprint: a
 * fixed four-cell CPU·RAM·DSK·GPU micro-equaliser on every row, at the same x
 * positions, so reading straight down a column answers "who is hot?" and an
 * all-hatch row reads "not reporting." Unknown is never a fake zero: a null axis
 * is hatched and sinks to its own bucket in every sort; a measured 0% is a real,
 * empty bar. Machine facts are mono, human copy sans, region labels the display
 * face.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { api } from '@/api/client'
import {
  Page,
  PageHeader,
  Badge,
  Button,
  StatusDot,
  KeyValue,
  Meter,
  EmptyState,
  Skeleton,
  type Tone,
} from '@/components/primitives'
import {
  QueryState,
  RowCheckbox,
  Dialog,
  TextField,
  Notice,
  CopyButton,
  useApiErrorMessage,
} from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { Icon } from '@/components/icon'
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  KeyRound,
  ChevronLeft,
  ArrowUp,
  ArrowDown,
  Trash2,
  X,
  GripVertical,
} from 'lucide-react'
import { useT, useLocale } from '@/i18n/context'
import { formatBytes, formatDateTime, formatDuration } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { useManualOrder, useDragAnnouncements } from '@/hooks/useManualOrder'
import { useSelectionMode } from '@/hooks/useSelectionMode'
import type { HostMetrics, HostStatus, ID, NodeStatus } from '@/types/domain'
import type { CLIStatus } from './fleet-telemetry'
import {
  useOverview,
  Freshness,
  MetricRow,
  GpuRows,
  frac,
  utilTone,
  fmtGiB,
  GiB,
  hostSampled,
  hostStale,
  hostLive,
  type HostResponse,
  type HostCapabilities,
  type CLIInfo,
  type OverviewHost,
} from './fleet-telemetry'
import './LiveOpsPage.css'
import './HostsPage.css'

/* ------------------------------------------------------------- Wire shapes */

/** api.HostCheck — one thing the verification established, or failed to. */
interface HostCheck {
  name: string
  ok: boolean
  skipped?: boolean
  detail?: string
}

/**
 * api.HostTestResponse — what a connection attempt found.
 *
 * This is where the host-key verdict comes from. `fingerprintChanged` and
 * `fingerprintUnknown` are separate booleans because they mean opposite things:
 * unknown is a machine nobody has vouched for yet, changed is a machine that no
 * longer matches the record, and only one of them is an incident.
 */
interface HostTestResponse {
  reachable: boolean
  target: string
  latencyMs: number
  detail?: string
  checked: string
  status: HostStatus
  checks: HostCheck[]
  observedFingerprint?: string
  expectedFingerprint?: string
  fingerprintChanged: boolean
  fingerprintUnknown: boolean
  authenticated: boolean
  canInstall: boolean
  capabilities?: HostCapabilities
}

/**
 * api.CLIInstallResponse — what the install endpoint answers.
 *
 * `installing` is the field that matters and the reason this type exists at all.
 * The endpoint has two success shapes: 202 with `installing: true` when work has
 * started on the host, and 200 with `installing: false` when the host already
 * runs the version that would have been sent. They are not the same outcome, and
 * treating them as one is how a bulk update reported six successes for six
 * hosts that were sent a copy of what they already had.
 */
interface CLIInstallResponse {
  hostId: ID
  provider: string
  version?: string
  installing: boolean
  detail?: string
}

/* ------------------------------------------------------------------ Tones */

function hostStatusTone(status: HostStatus): Tone {
  switch (status) {
    case 'ONLINE':
      return 'ok'
    case 'DEGRADED':
      return 'warn'
    case 'OFFLINE':
    case 'UNREACHABLE':
      return 'err'
    case 'BOOTSTRAPPING':
      return 'info'
    case 'UNKNOWN':
      return 'unknown'
  }
}

function nodeStatusTone(status: NodeStatus): Tone {
  switch (status) {
    case 'CONNECTED':
      return 'ok'
    case 'ENROLLING':
      return 'info'
    case 'STALE':
      return 'warn'
    case 'DISCONNECTED':
      return 'err'
    case 'NOT_INSTALLED':
      return 'neutral'
  }
}

/* -------------------------------------------------------- Sort + filter */

type SortKey =
  | 'health'
  | 'name'
  | 'pressure'
  | 'cpu'
  | 'ram'
  | 'disk'
  | 'gpu'
  | 'load'
  | 'sessions'
  | 'lastSeen'
type SortDir = 'asc' | 'desc'

const SORT_KEYS: SortKey[] = [
  'health',
  'name',
  'pressure',
  'cpu',
  'ram',
  'disk',
  'gpu',
  'load',
  'sessions',
  'lastSeen',
]

function isSortKey(v: string | null): v is SortKey {
  return v != null && (SORT_KEYS as string[]).includes(v)
}

/** The natural direction for a key, applied when `?dir` is absent: worst-first
 *  for health, A→Z for name, most-recent-first for last seen, and highest-first
 *  for every utilisation figure (the interesting end is the busy end). */
function defaultDir(key: SortKey): SortDir {
  return key === 'health' || key === 'name' || key === 'lastSeen' ? 'asc' : 'desc'
}

/** Severity order for the Sağlık sort: an unreachable host sorts before a merely
 *  degraded one, and both before a healthy one, so ascending puts problems on
 *  top. */
function statusRank(s: HostStatus): number {
  switch (s) {
    case 'OFFLINE':
    case 'UNREACHABLE':
      return 0
    case 'DEGRADED':
      return 1
    case 'BOOTSTRAPPING':
      return 2
    case 'UNKNOWN':
      return 3
    case 'ONLINE':
      return 4
  }
}

/** A host's pressure: the busiest of its measured CPU/RAM/GPU axes. Null when no
 *  axis is measured — never a zero, which would rank an unreported host as idle. */
function pressure(m: HostMetrics): number | null {
  const xs: number[] = []
  if (m.cpuUsedPercent != null) xs.push(m.cpuUsedPercent)
  if (m.memUsedPercent != null) xs.push(m.memUsedPercent)
  if (m.gpuUtilPercent != null) xs.push(m.gpuUtilPercent)
  return xs.length ? Math.max(...xs) : null
}

/** The comparable value for a numeric sort key, or null when unmeasured. Nulls
 *  are handled by the comparator, not defaulted here. */
function metricValue(h: OverviewHost, key: SortKey): number | null {
  const m = h.metrics
  switch (key) {
    case 'cpu':
      return m.cpuUsedPercent ?? null
    case 'ram':
      return m.memUsedPercent ?? null
    case 'disk':
      return m.diskUsedPercent ?? null
    case 'gpu':
      return m.gpuUtilPercent ?? null
    case 'pressure':
      return pressure(m)
    case 'load':
      return m.load1 ?? null
    case 'sessions':
      return m.activeSessions
    case 'lastSeen':
      return m.staleForSeconds ?? null
    case 'health':
      return statusRank(h.status)
    case 'name':
      return null
  }
}

/**
 * The one comparator, with the mandatory null discipline: a host missing the
 * sorted metric sinks to its own bucket at the END regardless of direction, so
 * an unreported value never masquerades as 0 or 100. Ties break on name.
 */
function compareHosts(a: OverviewHost, b: OverviewHost, key: SortKey, dir: SortDir): number {
  const nameCmp = a.name.localeCompare(b.name, 'tr')
  if (key === 'name') return dir === 'asc' ? nameCmp : -nameCmp
  const va = metricValue(a, key)
  const vb = metricValue(b, key)
  if (va == null && vb == null) return nameCmp
  if (va == null) return 1
  if (vb == null) return -1
  if (va !== vb) return (va < vb ? -1 : 1) * (dir === 'asc' ? 1 : -1)
  return nameCmp
}

const STATUS_ORDER: HostStatus[] = [
  'ONLINE',
  'DEGRADED',
  'BOOTSTRAPPING',
  'OFFLINE',
  'UNREACHABLE',
  'UNKNOWN',
]

/** The strip's status tally groups OFFLINE and UNREACHABLE — an operator reads
 *  them as one "not reachable" fact, and clicking the group filters to both. */
interface FilterFlags {
  gpu: boolean
  stale: boolean
  control: boolean
}

/** A managed machine's GPU inventory count, from the discovered capability list
 *  (not the live sample) — the honest "does this host have a GPU" answer. */
function gpuCount(h: HostResponse): number {
  return h.capabilities.gpus?.length ?? 0
}

/** A compact, mono presence readout from the seconds-since-last-seen clock.
 *  Absent → "hiç". Unit letters follow formatDuration's convention of stating
 *  the unit inline. */
function relSeconds(s: number): string {
  if (s < 60) return `${Math.round(s)}sn`
  if (s < 3600) return `${Math.round(s / 60)}dk`
  if (s < 86_400) return `${Math.round(s / 3600)}sa`
  return `${Math.round(s / 86_400)}g`
}

/** Debounces a value so the search box filters ~120ms after typing stops rather
 *  than on every keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}

/* ------------------------------------------------------------------ Page */

export function HostsPage() {
  const t = useT()
  const { hostId } = useParams<{ hostId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [adding, setAdding] = useState(false)
  // The rows a bulk action would act on.
  const [selected, setSelected] = useState<string[]>([])

  /**
   * Verification results, by host, for as long as the screen is open. Held here
   * rather than inside the focus panel so the alarm can stay a full-width band
   * above every panel — a host-key warning that scrolls out of view has failed
   * at its only job. Not cached in the query client either: this is the outcome
   * of one deliberate connection attempt, and serving it from a cache on a later
   * visit would present a stale verdict as a current one.
   */
  const [tests, setTests] = useState<Record<ID, HostTestResponse>>({})
  const recordTest = useCallback(
    (id: ID, result: HostTestResponse) => setTests((prev) => ({ ...prev, [id]: result })),
    [],
  )

  const overview = useOverview()
  const allHosts = useMemo(() => overview.data?.hosts ?? [], [overview.data])

  // --- Sort / filter state, mirrored to the URL query string. ---
  const sort: SortKey = isSortKey(searchParams.get('sort')) ? (searchParams.get('sort') as SortKey) : 'health'
  const dirParam = searchParams.get('dir')
  const dir: SortDir = dirParam === 'asc' || dirParam === 'desc' ? dirParam : defaultDir(sort)

  const statusParam = searchParams.get('status') ?? ''
  const statuses = useMemo(
    () => new Set(statusParam.split(',').filter(Boolean) as HostStatus[]),
    [statusParam],
  )
  const flagsParam = searchParams.get('flags') ?? ''
  const flags: FilterFlags = useMemo(() => {
    const set = new Set(flagsParam.split(',').filter(Boolean))
    return { gpu: set.has('gpu'), stale: set.has('stale'), control: set.has('control') }
  }, [flagsParam])

  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') ?? '')
  const debouncedQ = useDebounced(searchInput, 120)

  // Mirror the debounced query to the URL, without touching the other params.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (debouncedQ) next.set('q', debouncedQ)
        else next.delete('q')
        return next
      },
      { replace: true },
    )
  }, [debouncedQ, setSearchParams])

  const patchParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          mutate(next)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  /**
   * The operator's own order, laid over the column sort.
   *
   * Not instead of it: the sorted list is what a hand order rearranges, and rows
   * the order has never seen — a host enrolled since — keep their sorted place
   * after the ones it has. `changeSort` clears it, because asking for a different
   * column *is* asking for the automatic order back; that is the one thing an
   * operator can do here that means "sort this for me", so it would be perverse
   * for it to leave a hand order in charge.
   */
  const manual = useManualOrder('kimene.fleet.order')
  // The checkbox column is behind a toggle now; the toggle is what is permanent.
  // `clearChecked`, not `clearSelection`: this screen already has one of those and
  // it means the other thing — closing the focused host's panel. Ticked rows and a
  // focused machine are two different selections and always were.
  const clearChecked = useCallback(() => setSelected([]), [])
  const { selecting, toggle: toggleSelecting } = useSelectionMode(selected, clearChecked)

  const changeSort = useCallback(
    (key: SortKey) => {
      // Picking a column is the one act here that means "sort this for me", so it
      // takes the order back from a hand arrangement. Leaving a manual order in
      // charge would make the header look broken.
      manual.reset()
      patchParams((p) => {
        if (p.get('sort') === key) {
          // Re-selecting the active axis flips its direction.
          const cur = p.get('dir') === 'asc' || p.get('dir') === 'desc' ? p.get('dir') : defaultDir(key)
          const flipped = cur === 'asc' ? 'desc' : 'asc'
          if (flipped === defaultDir(key)) p.delete('dir')
          else p.set('dir', flipped)
        } else {
          p.set('sort', key)
          p.delete('dir') // a fresh key starts in its natural direction
        }
      })
    },
    [patchParams, manual],
  )


  const toggleStatuses = useCallback(
    (list: HostStatus[]) =>
      patchParams((p) => {
        const cur = new Set((p.get('status') ?? '').split(',').filter(Boolean))
        const active = list.some((s) => cur.has(s))
        for (const s of list) {
          if (active) cur.delete(s)
          else cur.add(s)
        }
        if (cur.size) p.set('status', [...cur].join(','))
        else p.delete('status')
      }),
    [patchParams],
  )

  const toggleFlag = useCallback(
    (flag: keyof FilterFlags) =>
      patchParams((p) => {
        const cur = new Set((p.get('flags') ?? '').split(',').filter(Boolean))
        if (cur.has(flag)) cur.delete(flag)
        else cur.add(flag)
        if (cur.size) p.set('flags', [...cur].join(','))
        else p.delete('flags')
      }),
    [patchParams],
  )

  const anyFilter = debouncedQ !== '' || statuses.size > 0 || flags.gpu || flags.stale || flags.control
  const clearFilters = useCallback(() => {
    setSearchInput('')
    patchParams((p) => {
      p.delete('q')
      p.delete('status')
      p.delete('flags')
    })
  }, [patchParams])

  // --- Derived host sets. ---
  const filtered = useMemo(() => {
    const q = debouncedQ.trim().toLowerCase()
    return allHosts.filter((h) => {
      if (statuses.size && !statuses.has(h.status)) return false
      if (flags.gpu && gpuCount(h) === 0) return false
      if (flags.stale && !hostStale(h.metrics)) return false
      if (flags.control && !h.isControlHost) return false
      if (q) {
        const hay = [
          h.name,
          h.address,
          h.sshConfigAlias,
          ...(h.labels ?? []),
          h.capabilities.os,
          h.capabilities.arch,
          h.nodeVersion,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [allHosts, statuses, flags, debouncedQ])

  const columnSorted = useMemo(
    () => [...filtered].sort((a, b) => compareHosts(a, b, sort, dir)),
    [filtered, sort, dir],
  )
  const sorted = useMemo(() => manual.apply(columnSorted), [columnSorted, manual])

  const sensors = useSensors(
    // 8px, so a drag is distinguishable from the click that opens a host — the
    // whole row is a pointer target here.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Named, not numbered: dnd-kit's own announcements are English and read out raw
  // ids, which is a leak rather than a description.
  const nameOf = useCallback(
    (id: string) => allHosts.find((h) => h.id === id)?.name ?? id,
    [allHosts],
  )
  const announcements = useDragAnnouncements(nameOf)

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const from = String(e.active.id)
      const over = e.over ? String(e.over.id) : null
      if (!over || from === over) return
      const ids = sorted.map((h) => h.id)
      const a = ids.indexOf(from)
      const b = ids.indexOf(over)
      if (a < 0 || b < 0) return
      // The whole visible order, not the two rows that moved: a partial order
      // would leave every other row to be re-sorted behind it.
      manual.set(arrayMove(ids, a, b))
    },
    [sorted, manual],
  )

  const selectedHost = hostId ? allHosts.find((h) => h.id === hostId) : undefined
  const selectedHidden = hostId != null && selectedHost != null && !sorted.some((h) => h.id === hostId)

  const selectHost = useCallback(
    (id: string) => {
      const qs = searchParams.toString()
      navigate(`/hosts/${id}${qs ? `?${qs}` : ''}`)
    },
    [navigate, searchParams],
  )
  const clearSelection = useCallback(() => {
    const qs = searchParams.toString()
    navigate(`/hosts${qs ? `?${qs}` : ''}`)
  }, [navigate, searchParams])

  const nodes = allHosts.length
  const online = allHosts.filter((h) => h.status === 'ONLINE').length
  const behind = allHosts.filter((h) =>
    (h.clis ?? []).some((c) => c.freshness === 'BEHIND'),
  ).length
  const subtitle = [
    t('fleet.online', { count: online }),
    behind > 0 ? t('fleet.cliBehindCount', { count: behind }) : null,
    sorted.length !== nodes ? t('fleet.shown', { shown: sorted.length, total: nodes }) : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const firstLoad = overview.isPending && !overview.data

  // Selection lives here rather than in the table, because the bulk bar acts on
  // it and both have to agree about what "3 selected" means. Ids that leave the
  // filtered set are dropped: a bulk action must never reach a row the operator
  // can no longer see.
  const visibleIds = useMemo(() => sorted.map((h) => h.id), [sorted])
  useEffect(() => {
    setSelected((prev) => {
      const kept = prev.filter((id) => visibleIds.includes(id))
      return kept.length === prev.length ? prev : kept
    })
  }, [visibleIds])

  const selectedHosts = useMemo(
    () => sorted.filter((h) => selected.includes(h.id)),
    [sorted, selected],
  )

  return (
    <Page
      header={
        <PageHeader
          title={t('fleet.title')}
          subtitle={subtitle}
          actions={
            <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
              {t('host.add')}
            </Button>
          }
        />
      }
    >
      <div className="k-fleet">
        {/* The alarm sits above every panel and is not dismissible. */}
        {allHosts.map((h) => {
          const result = tests[h.id]
          if (!result?.fingerprintChanged) return null
          return <FingerprintAlarm key={h.id} host={h} result={result} />
        })}

        {overview.data && <FleetSummary allHosts={allHosts} updatedAt={overview.dataUpdatedAt} />}

        {overview.isError && overview.data && (
          <Notice tone="warn">
            {t('fleet.staleData')}{' '}
            <button type="button" className="k-fleet__retry" onClick={() => void overview.refetch()}>
              {t('action.retry')}
            </button>
          </Notice>
        )}

        <FleetControls
          search={searchInput}
          onSearch={setSearchInput}
          statuses={statuses}
          statusCounts={allHosts}
          onToggleStatuses={toggleStatuses}
          flags={flags}
          onToggleFlag={toggleFlag}
          anyFilter={anyFilter}
          onClear={clearFilters}
          selecting={selecting}
          onToggleSelecting={toggleSelecting}
        />

        {firstLoad ? (
          <div className="k-fleet__skeltable">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={34} />
            ))}
          </div>
        ) : allHosts.length === 0 ? (
          <QueryState
            pending={false}
            error={overview.error}
            onRetry={() => void overview.refetch()}
            empty
            emptyTitle={t('host.empty')}
            emptyHint={t('host.emptyHint')}
            emptyAction={
              <Button variant="primary" onClick={() => setAdding(true)}>
                {t('host.add')}
              </Button>
            }
          >
            {null}
          </QueryState>
        ) : sorted.length === 0 ? (
          <EmptyState
            title={t('fleet.noMatches')}
            action={
              <Button size="sm" onClick={clearFilters}>
                {t('common.clearFilters')}
              </Button>
            }
          />
        ) : (
          <div className="k-fleet__work">
            <div className="k-fleet__main">
              {/* Above the table and always present. It used to appear only once
                  a row was ticked, so nothing on the screen said the rows could
                  be ticked at all — an operator asked twice for a capability
                  that was already there and had no affordance. */}
              {selecting && (
                <FleetBulkBar
                  hosts={selectedHosts}
                  onClear={() => setSelected([])}
                  onResult={recordTest}
                />
              )}
              {/* On screen only while there is an order to leave, and beside the
                  table it reorders. A decision needs a visible way back from
                  where it was taken. */}
              {manual.order && (
                <button type="button" className="k-fleet__reorder" onClick={manual.reset}>
                  {t('order.manual')} · {t('order.reset')}
                </button>
              )}
              <DndContext sensors={sensors} onDragEnd={onDragEnd} accessibility={{ announcements }}>
                <HostTable
                  hosts={sorted}
                  sort={sort}
                  dir={dir}
                  onSort={changeSort}
                  focused={hostId}
                  onFocus={selectHost}
                  selected={selected}
                  selecting={selecting}
                  onSelect={setSelected}
                />
              </DndContext>
            </div>

            {/* Beside the table rather than under it. Opening a host used to
                push a nine-hundred-pixel document below the rows and take the
                fleet off the screen — so comparing two machines meant scrolling
                between them from memory. */}
            {hostId && (
              <HostFocus
                key={hostId}
                hostId={hostId}
                host={selectedHost}
                hidden={selectedHidden}
                result={tests[hostId]}
                onResult={recordTest}
                onClose={clearSelection}
              />
            )}
          </div>
        )}
      </div>

      {adding && <AddHostDialog onClose={() => setAdding(false)} />}
    </Page>
  )
}

/* ------------------------------------------------------------ Fleet summary */

/** A small caps section label. Used by the focus panel's sections. */
function Eyebrow({ children }: { children: ReactNode }) {
  return <span className="k-fleet__eyebrow">{children}</span>
}


/**
 * One line of headline facts, and the three gauges that are genuinely a fleet
 * property rather than a host's.
 *
 * It replaces a four-region strip whose regions were DURUM, BASINÇ, CANLI
 * ÖLÇÜMLER and ENVANTER. Two of those were the same numbers twice — the gauges
 * and the per-host bars said the same thing — and "basınç" was a single bar
 * spanning the whole width that no operator could act on: it answered "is the
 * fleet busy" with one number for seven machines, when the only useful form of
 * that question is *which* machine.
 *
 * What survives is what an operator scans for before doing anything: how many
 * machines answer, how many are running an old CLI, and how much iron there is.
 */
/** Fleet memory at the scale a person says it in. `fmtGiB` is right for one
 *  machine and wrong for a fleet: seven of them add up to "2014G", which is a
 *  number nobody uses out loud. */
function fmtFleetMemory(bytes: number): string {
  const gib = bytes / GiB
  return gib >= 1024 ? `${(gib / 1024).toFixed(1)} TiB` : `${Math.round(gib)} GiB`
}

function FleetSummary({ allHosts, updatedAt }: { allHosts: OverviewHost[]; updatedAt: number }) {
  const t = useT()

  const cores = allHosts.reduce((n, h) => n + (h.capabilities.cpuCores ?? 0), 0)
  const memory = allHosts.reduce((n, h) => n + (h.capabilities.memoryBytes ?? 0), 0)
  const sessions = allHosts.reduce((n, h) => n + (h.metrics.activeSessions ?? 0), 0)

  // Averaged over the hosts that actually reported. A host with no sample is
  // left out rather than counted as zero, which would drag the fleet average
  // toward "idle" every time a machine went quiet — and the denominator is
  // published beside the number so "2% of seven" cannot be read as "2% of two".
  const gauge = (pick: (h: OverviewHost) => number | null | undefined) => {
    const vals = allHosts.map(pick).filter((v): v is number => typeof v === 'number')
    if (vals.length === 0) return { mean: null, peak: null, report: 0, total: allHosts.length }
    return {
      mean: vals.reduce((a, b) => a + b, 0) / vals.length,
      peak: Math.max(...vals),
      report: vals.length,
      total: allHosts.length,
    }
  }

  // One line, because that is what these five numbers are worth. It was a
  // 245px band across the full width and nine per cent of it was ink — three
  // figures stacked down the left of an otherwise empty rectangle, which is
  // most of the vertical space an operator had before the first row of data.
  return (
    <section className="k-fleet__summary" aria-label={t('fleet.region.status')}>
      <p className="k-fleet__headline">
        <span className="k-mono">{cores}</span> {t('fleet.totalCores').toLocaleLowerCase()}
        <span className="k-fleet__dot">·</span>
        <span className="k-mono">{fmtFleetMemory(memory)}</span>{' '}
        {t('fleet.totalMemory').toLocaleLowerCase()}
        <span className="k-fleet__dot">·</span>
        <span className="k-mono">{sessions}</span> {t('fleet.totalSessions').toLocaleLowerCase()}
      </p>
      <div className="k-fleet__gauges">
        <FleetGauge label={t('live.metric.cpu')} g={gauge((h) => h.metrics.cpuUsedPercent)} />
        <FleetGauge label={t('live.metric.ram')} g={gauge((h) => h.metrics.memUsedPercent)} />
        <FleetGauge label={t('live.metric.gpu')} g={gauge((h) => h.metrics.gpuUtilPercent)} />
      </div>
      <Freshness updatedAt={updatedAt} />
    </section>
  )
}

function StatusChip({
  tone,
  label,
  count,
  active,
  onClick,
}: {
  tone: Tone
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={clsx('k-fleet__schip', active && 'k-fleet__schip--on')}
      onClick={onClick}
      aria-pressed={active}
    >
      <StatusDot tone={tone} label={label} />
      <span className="k-fleet__schip-lbl k-mono">{label}</span>
      <span className="k-fleet__schip-n k-mono">{count}</span>
    </button>
  )
}

function FleetGauge({
  label,
  g,
}: {
  label: string
  g: { mean: number | null; peak: number | null; report: number; total: number }
}) {
  const t = useT()
  return (
    <div className="k-fleet__gauge">
      <div className="k-fleet__gauge-head">
        <span className="k-fleet__gauge-lbl">{label}</span>
        <span className="k-mono k-fleet__gauge-pct">{g.mean == null ? '—' : `${Math.round(g.mean)}%`}</span>
      </div>
      <Meter value={frac(g.mean)} tone={utilTone(g.mean)} unknownLabel="—" ariaLabel={label} />
      <span className="k-mono k-fleet__gauge-den">
        {g.peak != null ? `${t('fleet.peak', { pct: Math.round(g.peak) })} · ` : ''}
        {t('fleet.reporting', { n: g.report, m: g.total })}
      </span>
    </div>
  )
}

/* ------------------------------------------------------------- Controls */

function FleetControls({
  search,
  onSearch,
  statuses,
  statusCounts,
  onToggleStatuses,
  flags,
  onToggleFlag,
  anyFilter,
  onClear,
  selecting,
  onToggleSelecting,
}: {
  search: string
  onSearch: (v: string) => void
  statuses: Set<HostStatus>
  statusCounts: OverviewHost[]
  onToggleStatuses: (list: HostStatus[]) => void
  flags: FilterFlags
  onToggleFlag: (f: keyof FilterFlags) => void
  anyFilter: boolean
  onClear: () => void
  /** Whether the table is offering its rows for selection. */
  selecting: boolean
  onToggleSelecting: () => void
}) {
  const t = useT()
  const counts = useMemo(() => {
    const map = new Map<HostStatus, number>()
    for (const h of statusCounts) map.set(h.status, (map.get(h.status) ?? 0) + 1)
    return map
  }, [statusCounts])

  return (
    <div className="k-fleet__controls">
      <TextField
        label={t('fleet.search')}
        labelHidden
        type="search"
        mono
        value={search}
        onChange={onSearch}
        placeholder={t('fleet.searchPlaceholder')}
        name="fleet-search"
      />

      <div className="k-fleet__chips">
          {STATUS_ORDER.map((s) => {
            const count = counts.get(s) ?? 0
            if (count === 0) return null
            return (
              <StatusChip
                key={s}
                tone={hostStatusTone(s)}
                label={s}
                count={count}
                active={statuses.has(s)}
                onClick={() => onToggleStatuses([s])}
              />
            )
          })}
        <FilterToggle label={t('fleet.filter.gpu')} active={flags.gpu} onClick={() => onToggleFlag('gpu')} />
        <FilterToggle label={t('fleet.filter.stale')} active={flags.stale} onClick={() => onToggleFlag('stale')} />
        <FilterToggle label={t('fleet.filter.control')} active={flags.control} onClick={() => onToggleFlag('control')} />
        <Button variant="ghost" size="sm" disabled={!anyFilter} onClick={onClear}>
          {t('fleet.clear')}
        </Button>
        {/* Not a filter, but this is where the row's own controls live and it is
            the permanent affordance for selection now that the checkbox column is
            not. Same toggle recipe as the three beside it. */}
        <FilterToggle
          label={t(selecting ? 'order.selectDone' : 'order.select')}
          active={selecting}
          onClick={onToggleSelecting}
        />
      </div>
    </div>
  )
}

function FilterToggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={clsx('k-fleet__toggle', active && 'k-fleet__toggle--on')}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  )
}

/* ---------------------------------------------------------- Bulk actions */

/** What a bulk run did, per host, so the summary can say what was skipped and
 *  why rather than reporting a bare count. */
interface BulkOutcome {
  ok: number
  skipped: number
  failed: number
  detail?: string
}

/**
 * Acts on the selected hosts, one at a time.
 *
 * Sequential deliberately. Each of these opens an SSH connection, and a CLI
 * install ships a 300 MB binary the control plane holds in memory while it
 * writes it — seven of those at once is a memory spike and seven simultaneous
 * connections to the same fleet. Nothing here is latency-critical enough to pay
 * for that.
 *
 * The summary reports three outcomes, not two: "skipped" is its own answer
 * because a host whose CLI is already current was never asked to do anything,
 * and folding it into either success or failure would misreport the run.
 */
function FleetBulkBar({
  hosts,
  onClear,
  onResult,
}: {
  hosts: OverviewHost[]
  onClear: () => void
  onResult: (id: ID, result: HostTestResponse) => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const [running, setRunning] = useState<'test' | 'cli' | null>(null)
  const [progress, setProgress] = useState('')
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
    void queryClient.invalidateQueries({ queryKey: ['hosts'] })
  }

  const runTests = async () => {
    setRunning('test')
    setOutcome(null)
    const tally: BulkOutcome = { ok: 0, skipped: 0, failed: 0 }
    for (const [i, host] of hosts.entries()) {
      setProgress(t('fleet.bulkProgress', { done: i + 1, total: hosts.length, host: host.name }))
      try {
        const result = await api.post<HostTestResponse>(`/hosts/${host.id}/test`)
        onResult(host.id, result)
        if (result.reachable && !result.fingerprintChanged) tally.ok++
        else tally.failed++
      } catch (err) {
        tally.failed++
        tally.detail = describe(err).title
      }
    }
    invalidate()
    setRunning(null)
    setProgress('')
    setOutcome(tally)
  }

  const runCLIUpdates = async () => {
    setRunning('cli')
    setOutcome(null)
    const tally: BulkOutcome = { ok: 0, skipped: 0, failed: 0 }
    for (const [i, host] of hosts.entries()) {
      const behind = (host.clis ?? []).filter((c) => c.freshness === 'BEHIND')
      if (behind.length === 0) {
        // Already current. Reinstalling would restart a CLI for no reason.
        tally.skipped++
        continue
      }
      setProgress(t('fleet.bulkProgress', { done: i + 1, total: hosts.length, host: host.name }))
      for (const cli of behind) {
        try {
          // The server answers 200 with installing:false when the host already
          // runs the version that would be sent. Counting that as a success is
          // how "6 tamam" came to mean "6 hosts were sent what they already
          // had" — the tally looked like progress and nothing had changed.
          const started = await api.post<CLIInstallResponse>(
            `/hosts/${host.id}/cli/${cli.provider}/install`,
          )
          if (started?.installing === false) tally.skipped++
          else tally.ok++
        } catch (err) {
          tally.failed++
          // Which CLI, not just what went wrong. Two providers per host means a
          // bare message leaves the operator unable to tell whether it was
          // claude on one host or codex on all of them.
          tally.detail = `${cli.provider}: ${describe(err).title}`
        }
      }
    }
    invalidate()
    setRunning(null)
    setProgress('')
    setOutcome(tally)
  }

  const busy = running !== null
  const none = hosts.length === 0

  return (
    <div className="k-bulkbar" role="region" aria-label={t('fleet.bulkTitle')}>
      {/* What it is waiting for, rather than an empty count. A disabled row of
          buttons with "0 selected" beside it reads as broken; this reads as an
          instruction. */}
      <span className={clsx('k-bulkbar__count', none && 'k-bulkbar__count--idle')}>
        {none ? t('fleet.bulkNone') : t('fleet.bulkSelected', { count: hosts.length })}
      </span>
      <Button
        size="sm"
        busy={running === 'test'}
        disabled={busy || none}
        onClick={() => void runTests()}
      >
        {t('host.testConnection')}
      </Button>
      <Button
        size="sm"
        busy={running === 'cli'}
        disabled={busy || none}
        onClick={() => void runCLIUpdates()}
      >
        {t('host.updateCLI')}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy || none} onClick={onClear}>
        {t('fleet.bulkClear')}
      </Button>
      {progress && <span className="k-bulkbar__progress">{progress}</span>}
      {outcome && (
        <span className={clsx('k-bulkbar__result', outcome.failed > 0 && 'k-bulkbar__result--err')}>
          {t('fleet.bulkDone', {
            ok: outcome.ok,
            skipped: outcome.skipped,
            failed: outcome.failed,
          })}
          {outcome.detail ? ` · ${outcome.detail}` : ''}
        </span>
      )}
    </div>
  )
}

/* ------------------------------------------------------------- Host table */

/** The columns, in the order an operator reads them: who, is it well, how hard
 *  is it working, what can it run, when did we last hear from it. */
const COLUMNS: Array<{ key: SortKey; label: MessageKey; numeric?: boolean }> = [
  { key: 'name', label: 'fleet.col.host' },
  { key: 'cpu', label: 'live.metric.cpu', numeric: true },
  { key: 'ram', label: 'live.metric.ram', numeric: true },
  { key: 'disk', label: 'fleet.col.disk', numeric: true },
  { key: 'sessions', label: 'fleet.col.sessions', numeric: true },
  { key: 'lastSeen', label: 'fleet.col.lastSeen', numeric: true },
]

/**
 * The fleet as a table.
 *
 * It replaces a list whose utilisation was four bars about 14 pixels wide, one
 * per axis, with no numbers — a "fingerprint" that could be told apart from
 * another host's only by staring. Measured against what it was for: an operator
 * asking "is SRV4 busy" could not read the answer off it.
 *
 * So the axes get their own columns with the figure spelled out, and a meter
 * behind it for shape. `Meter` draws an unmeasured axis hatched rather than
 * empty, which is the distinction the bars could not make: a host that reported
 * 0% and a host that reported nothing looked identical.
 */
function HostTable({
  hosts,
  sort,
  dir,
  onSort,
  focused,
  onFocus,
  selected,
  selecting,
  onSelect,
}: {
  hosts: OverviewHost[]
  sort: SortKey
  dir: SortDir
  onSort: (k: SortKey) => void
  focused?: string
  onFocus: (id: string) => void
  selected: string[]
  /** Whether the table is offering its rows for selection. */
  selecting: boolean
  onSelect: (ids: string[]) => void
}) {
  const t = useT()
  const allSelected = hosts.length > 0 && selected.length === hosts.length
  const someSelected = selected.length > 0 && !allSelected

  // Memoised. A new array every render makes dnd-kit re-register its items
  // mid-gesture, and the drag ends where it began.
  const rowIds = useMemo(() => hosts.map((h) => h.id), [hosts])

  const toggleAll = () => onSelect(allSelected ? [] : hosts.map((h) => h.id))
  const toggleOne = (id: string) =>
    onSelect(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])

  return (
    <div className="k-fleet__tablewrap">
      <table className="k-fleet__table">
        <thead>
          <tr>
            <th scope="col" className="k-fleet__sel">
              {selecting && (
                <RowCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  label={t('fleet.selectAll')}
                />
              )}
            </th>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={clsx(col.numeric && 'k-fleet__num')}
                aria-sort={sort === col.key ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" className="k-fleet__sortbtn" onClick={() => onSort(col.key)}>
                  {t(col.label)}
                  {sort === col.key && (
                    <Icon icon={dir === 'asc' ? ArrowUp : ArrowDown} size={12} />
                  )}
                </button>
              </th>
            ))}
            {/* Not sortable: a version is not an ordering an operator wants the
                fleet arranged by, and "which of these is out of date" is
                answered by the badge, not by sorting. */}
            <th scope="col">{t('fleet.col.cli')}</th>
          </tr>
        </thead>
        <tbody>
          <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
            {hosts.map((h) => (
              <HostTableRow
                key={h.id}
                host={h}
                focused={h.id === focused}
                selected={selected.includes(h.id)}
                selecting={selecting}
                onToggle={() => toggleOne(h.id)}
                onFocus={() => onFocus(h.id)}
              />
            ))}
          </SortableContext>
        </tbody>
      </table>
    </div>
  )
}

function HostTableRow({
  host,
  focused,
  selected,
  selecting,
  onToggle,
  onFocus,
}: {
  host: OverviewHost
  focused: boolean
  selected: boolean
  selecting: boolean
  onToggle: () => void
  onFocus: () => void
}) {
  const t = useT()
  const m = host.metrics
  const stale = hostStale(m)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id: host.id })

  return (
    // The whole row opens the host. It used to be the name alone — a 48px target
    // in a 1010px row — so an operator aiming at a machine had to aim at four
    // characters of its name. The button stays: it is the accessible control and
    // the keyboard path, and this only widens where a pointer may land.
    <tr
      ref={setNodeRef}
      className={clsx(
        'k-fleet__row',
        focused && 'k-fleet__row--on',
        selected && 'k-fleet__row--sel',
        isDragging && 'k-fleet__row--dragging',
      )}
      style={{
        // Inline, never a transition: base.css flattens every transition-duration
        // to 0.01ms under prefers-reduced-motion, so a transition-driven drag jumps.
        transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
      }}
      onClick={onFocus}
    >
      {/* The selector is the exception, and has to be: ticking a box is a
          different act from opening the host, and a click that did both would
          make selecting several rows impossible without the panel following
          along behind. */}
      <td className="k-fleet__sel" onClick={(e) => e.stopPropagation()}>
        {/* Both row-level controls in the one cell that already stops a click from
            reaching the row. A grip in a column of its own would have become the
            table's `:last-child`, and the CLI badges' width is pinned by that
            selector — a new column would have quietly unpinned it. */}
        {/* One or the other, never both: choosing rows and arranging them are two
            jobs, and two 24px targets in one narrow cell make each harder to hit. */}
        {selecting ? (
          <RowCheckbox checked={selected} onChange={onToggle} label={host.name} />
        ) : (
          <span
            className="k-fleet__grip"
            aria-label={t('order.move', { name: host.name })}
            title={t('order.move', { name: host.name })}
            {...attributes}
            {...listeners}
          >
            <Icon icon={GripVertical} size={13} />
          </span>
        )}
      </td>
      <td>
        <button type="button" className="k-fleet__namebtn" onClick={onFocus}>
          <StatusDot tone={hostStatusTone(host.status)} live={hostLive(host)} label={host.status} />
          <span className="k-fleet__name">{host.name}</span>
          {host.isControlHost && (
            <Badge tone="accent" subtle mono>
              {t('fleet.control')}
            </Badge>
          )}
          {stale && (
            <Badge tone="warn" subtle>
              {t('fleet.stale')}
            </Badge>
          )}
        </button>
      </td>
      <MetricCell value={m.cpuUsedPercent} label={t('live.metric.cpu')} />
      <MetricCell value={m.memUsedPercent} label={t('live.metric.ram')} />
      <MetricCell value={m.diskUsedPercent} label={t('fleet.col.disk')} />
      <td className="k-fleet__num k-mono">{m.activeSessions || '—'}</td>
      <td className="k-fleet__num k-mono">
        {m.staleForSeconds == null ? '—' : relSeconds(m.staleForSeconds)}
      </td>
      <td className="k-fleet__cli">
        <CliCell host={host} />
      </td>
    </tr>
  )
}

/** One utilisation axis: the number, and a meter behind it for shape. Null is
 *  hatched rather than drawn as an empty bar — "not measured" is not "idle". */
function MetricCell({ value, label }: { value?: number | null; label: string }) {
  return (
    <td className="k-fleet__num">
      <span className="k-fleet__cell">
        <span className="k-mono k-fleet__pct">{value == null ? '—' : `${Math.round(value)}%`}</span>
        <Meter value={frac(value)} tone={utilTone(value)} unknownLabel="—" ariaLabel={label} />
      </span>
    </td>
  )
}

/** What this host can run, and whether it is current. Only BEHIND earns a
 *  badge: a tick beside every host would bury the one that is out of date. */
function CliCell({ host }: { host: OverviewHost }) {
  const t = useT()
  const claude = host.capabilities.claudeCli
  const codex = host.capabilities.codexCli
  const status = (p: string) => (host.clis ?? []).find((c) => c.provider === p)

  const one = (name: string, info: CLIInfo | undefined, p: string) => {
    if (!info?.installed) {
      return (
        <Badge key={name} tone="neutral" subtle mono>
          {name} {t('host.missing')}
        </Badge>
      )
    }
    const s = status(p)
    return (
      <span key={name} className="k-toolchip">
        <Badge tone="ok" subtle mono>
          {name} {shortVersion(info.version)}
        </Badge>
        {s?.freshness === 'BEHIND' && s.latest && (
          <Badge tone="warn" subtle mono>
            {t('host.cliBehind', { latest: s.latest })}
          </Badge>
        )}
      </span>
    )
  }

  return (
    <span className="k-fleet__clis">
      {one('claude', claude, 'claude')}
      {one('codex', codex, 'codex')}
    </span>
  )
}

/** `claude --version` answers "2.1.233 (Claude Code)". The product name is not
 *  a version and repeating it on every row costs the column its legibility. */
function shortVersion(v?: string): string {
  if (!v) return '—'
  return v.trim().split(/\s+/)[0] ?? v
}

/* --------------------------------------------------------------- Host focus */

function HostFocus({
  hostId,
  host,
  hidden,
  result,
  onResult,
  onClose,
}: {
  hostId: string
  host?: OverviewHost
  hidden: boolean
  result?: HostTestResponse
  onResult: (id: ID, result: HostTestResponse) => void
  onClose: () => void
}) {
  const t = useT()
  const { locale } = useLocale()

  // The overview carries identity + capabilities + metrics for every host, so
  // the focus reads off it directly. The single-host GET is only a fallback for
  // a deep link opened before the overview has loaded.
  const fallback = useQuery({
    queryKey: ['host', hostId],
    queryFn: () => api.get<HostResponse>(`/hosts/${hostId}`),
    enabled: host == null,
  })

  const h: OverviewHost | HostResponse | undefined = host ?? fallback.data
  if (!h) {
    return (
      <QueryState pending={fallback.isPending} error={fallback.error} onRetry={() => void fallback.refetch()}>
        {null}
      </QueryState>
    )
  }

  // Metrics come only from the overview host; the deep-link fallback is a plain
  // HostResponse with no sample, so its gauges render hatched.
  const m = host?.metrics
  const caps = h.capabilities
  const sampled = m != null && hostSampled(m)
  const stale = m != null && hostStale(m)

  const sampleBadge = !sampled ? (
    <Badge tone="unknown" subtle>
      {t('host.noSample')}
    </Badge>
  ) : stale ? (
    <Badge tone="warn" subtle>
      {t('host.staleFor', { rel: m?.metricsStaleForSeconds != null ? relSeconds(m.metricsStaleForSeconds) : '—' })}
    </Badge>
  ) : null

  const gpus = gpuCount(h)
  const gpuModel = formatGpus(caps.gpus)

  return (
    <div className={clsx('k-fleet__focus', hidden && 'k-fleet__focus--hidden')}>
      <header className="k-fleet__focushead">
        <button type="button" className="k-fleet__back" onClick={onClose} aria-label={t('fleet.back')}>
          <Icon icon={ChevronLeft} size={15} />
          <span className="k-fleet__back-lbl">{t('fleet.back')}</span>
        </button>
        <div className="k-fleet__focusid">
          <div className="k-fleet__focustitle">
            <StatusDot
              tone={hostStatusTone(h.status)}
              live={host ? hostLive(host) : false}
              label={h.status}
            />
            <span className="k-fleet__focusname">{h.name}</span>
            <Badge tone={nodeStatusTone(h.nodeStatus)} subtle>
              {h.nodeStatus}
            </Badge>
            {h.isControlHost && (
              <Badge tone="accent" subtle mono>
                {t('fleet.control')}
              </Badge>
            )}
          </div>
          {/* Identity only. The node version used to be appended here behind a
              middle dot, which made "root@172.19.16.51:22 · 2f6e23d" read as one
              address — an unlabelled hash is indistinguishable from a port or a
              hostname suffix. It is a labelled row in the connection section
              now, where a version can be told apart from a machine. */}
          <div className="k-fleet__focusaddr k-mono">{hostAddress(h)}</div>
          <div className="k-fleet__focusagent">
            <AgentVersion host={h} />
          </div>
        </div>
        <button
          type="button"
          className="k-fleet__close"
          onClick={onClose}
          aria-label={t('action.close')}
        >
          <Icon icon={X} size={15} />
        </button>
      </header>

      {hidden && <Notice tone="info">{t('host.hiddenInList')}</Notice>}

      <section className="k-fleet__section">
        <div className="k-fleet__sectionhead">
          <Eyebrow>{t('fleet.region.liveMetrics')}</Eyebrow>
          {sampleBadge}
        </div>
        <div className="k-hm__rows">
          <MetricRow label={t('live.metric.cpu')} pct={m?.cpuUsedPercent} />
          <MetricRow
            label={t('live.metric.ram')}
            pct={m?.memUsedPercent}
            detail={
              m?.memUsedBytes && (m?.memoryBytes ?? caps.memoryBytes)
                ? `${fmtGiB(m.memUsedBytes)} / ${fmtGiB(m.memoryBytes ?? caps.memoryBytes)}G`
                : undefined
            }
          />
          <MetricRow
            label={t('live.metric.disk')}
            pct={m?.diskUsedPercent}
            detail={m?.diskBytes ?? caps.diskBytes ? `${fmtGiB(m?.diskBytes ?? caps.diskBytes)}G` : undefined}
          />
        </div>
        {/* Two counts, not one sentence. Run together behind a middle dot they
            read as "Oturum 0 · Hesap 0" — four tokens with no telling which
            number belonged to which word. */}
        <div className="k-fleet__counts">
          <div className="k-fleet__count">
            <span className="k-fleet__count-lbl">{t('fleet.focus.sessions')}</span>
            <span className="k-fleet__count-val k-mono">{m?.activeSessions ?? '—'}</span>
          </div>
          <div className="k-fleet__count">
            <span className="k-fleet__count-lbl">{t('fleet.focus.accounts')}</span>
            <span className="k-fleet__count-val k-mono">{m?.accountsHosted ?? '—'}</span>
          </div>
        </div>
      </section>

      <section className="k-fleet__section">
        <Eyebrow>{t('fleet.region.gpu')}</Eyebrow>
        <div className="k-hm__rows">
          {gpus === 0 ? (
            <div className="k-fleet__nogpu">{t('host.noGpu')}</div>
          ) : m?.gpus && m.gpus.length > 0 ? (
            <GpuRows gpus={m.gpus} t={t} />
          ) : m?.gpuUtilPercent != null ? (
            <>
              {gpuModel && <div className="k-hm__gpucap k-mono">{gpuModel}</div>}
              <MetricRow
                label={t('live.metric.gpu')}
                pct={m.gpuUtilPercent}
                detail={
                  [
                    m.gpuTempC != null ? `${Math.round(m.gpuTempC)}°C` : null,
                    m.gpuMemUsedMb ? `${(m.gpuMemUsedMb / 1024).toFixed(1)}G` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || undefined
                }
              />
            </>
          ) : (
            <>
              {gpuModel && <div className="k-hm__gpucap k-mono">{gpuModel}</div>}
              <MetricRow label={t('live.metric.gpu')} pct={undefined} />
            </>
          )}
        </div>
      </section>

      <section className="k-fleet__section">
        <Eyebrow>{t('fleet.region.inventory')}</Eyebrow>
        {caps.discoveredAt ? (
          <>
            <KeyValue
              items={[
                { key: 'os', label: t('host.os'), value: caps.os ?? '—', mono: true },
                { key: 'arch', label: t('host.arch'), value: caps.arch ?? '—', mono: true },
                ...(caps.kernelInfo
                  ? [{ key: 'kernel', label: t('host.kernel'), value: caps.kernelInfo, mono: true }]
                  : []),
                {
                  key: 'cores',
                  label: t('host.cores'),
                  value: caps.cpuCores != null ? String(caps.cpuCores) : '—',
                  mono: true,
                },
                {
                  key: 'mem',
                  label: t('host.memory'),
                  value: caps.memoryBytes != null ? formatBytes(caps.memoryBytes) : '—',
                  mono: true,
                },
                {
                  key: 'disk',
                  label: t('host.disk'),
                  value: caps.diskBytes != null ? formatBytes(caps.diskBytes) : '—',
                  mono: true,
                },
                { key: 'gpu', label: t('host.gpu'), value: gpuModel ?? t('host.noGpu') },
                {
                  key: 'discovered',
                  label: t('host.capabilities'),
                  value: formatDateTime(caps.discoveredAt, locale),
                },
              ]}
            />
          </>
        ) : (
          <Notice tone="unknown">{t('host.notDiscovered')}</Notice>
        )}
      </section>

      {/* Connection facts, which the header can only hint at. The pinned host
          key in particular used to appear nowhere except inside an alarm — so
          the one time an operator could read it was the one time it had already
          changed. */}
      <section className="k-fleet__section">
        <Eyebrow>{t('fleet.region.connection')}</Eyebrow>
        <KeyValue
          items={[
            // Not the address: it is the line under the name, three rows up, and
            // a fact stated twice in one panel is one an operator reads twice to
            // check they are the same.
            { key: 'auth', label: t('host.authMethod'), value: h.authMethod, mono: true },
            {
              key: 'nodever',
              label: t('host.agentVersion'),
              value: h.nodeVersion ?? '—',
              mono: true,
            },
            {
              key: 'key',
              label: t('host.fingerprint'),
              value: h.hostKeyFingerprint ?? t('host.fingerprintNone'),
              mono: true,
            },
          ]}
        />
      </section>

      <section className="k-fleet__section">
        <Eyebrow>{t('fleet.region.tools')}</Eyebrow>
        {caps.discoveredAt ? (
          <>
            <div className="k-fleet__tools">
              <ToolChip label="tmux" present={caps.hasTmux} />
              <ToolChip label="git" present={caps.hasGit} />
              <ToolChip
                label="claude"
                present={caps.claudeCli?.installed ?? false}
                version={caps.claudeCli?.version}
                cli={h.clis?.find((c) => c.provider === 'claude')}
              />
              <ToolChip
                label="codex"
                present={caps.codexCli?.installed ?? false}
                version={caps.codexCli?.version}
                cli={h.clis?.find((c) => c.provider === 'codex')}
              />
            </div>
          </>
        ) : (
          <Notice tone="unknown">{t('host.notDiscovered')}</Notice>
        )}
      </section>

      <section className="k-fleet__section">
        <HostActions host={h} result={result} onResult={onResult} />
        {result && <HostTestReport result={result} />}
      </section>
    </div>
  )
}

/* ------------------------------------------------------------ Small parts */

/**
 * How a host is reached, as one string.
 *
 * The fallback is the point: a host configured through an ssh_config alias has
 * no address, and the panel used to render `${user}@${address}:${port}` for it
 * regardless — producing "root@:22", which names no machine at all.
 */
function hostAddress(h: HostResponse | OverviewHost): string {
  return h.address ? `${h.user}@${h.address}:${h.port}` : (h.sshConfigAlias ?? '—')
}

/** Collapses a GPU list to counts — "4 × Tesla P40" — so a machine with four
 *  identical cards reads as one fact, not the same name repeated four times. */
function formatGpus(gpus?: string[]): string | undefined {
  if (!gpus || gpus.length === 0) return undefined
  const counts = new Map<string, number>()
  for (const g of gpus) counts.set(g, (counts.get(g) ?? 0) + 1)
  return Array.from(counts, ([name, n]) => (n > 1 ? `${n} × ${name}` : name)).join(' · ')
}

/** A connection check's human name. The wire names are machine tokens; the
 *  operator reads what the check *means*, not the field it came from. */
function checkLabel(name: string, t: ReturnType<typeof useT>): string {
  switch (name) {
    case 'tcp':
      return t('host.check.tcp')
    case 'hostKey':
      return t('host.check.hostKey')
    case 'authentication':
      return t('host.check.authentication')
    case 'installPaths':
      return t('host.check.installPaths')
    case 'inventory':
      return t('host.check.inventory')
    case 'sharedAccounts':
      return t('host.check.sharedAccounts')
    default:
      return name
  }
}

function ToolChip({
  label,
  present,
  version,
  cli,
}: {
  label: string
  present: boolean
  version?: string
  cli?: CLIStatus
}) {
  const t = useT()
  return (
    <span className="k-toolchip">
      <Badge tone={present ? 'ok' : 'neutral'} subtle mono>
        {label} {present ? (version ?? t('host.installed')) : t('host.missing')}
      </Badge>
      {/* Only "behind" earns a second badge. CURRENT is the expected state and a
          green tick beside every host is noise; UNKNOWN says nothing because
          nothing was compared, and saying so on every chip would drown the one
          host that is actually out of date. */}
      {cli?.freshness === 'BEHIND' && cli.latest && (
        <Badge tone="warn" subtle mono>
          {t('host.cliBehind', { latest: cli.latest })}
        </Badge>
      )}
    </span>
  )
}

/* ------------------------------------------------------- Fingerprint alarm */

function FingerprintAlarm({ host, result }: { host: HostResponse; result: HostTestResponse }) {
  const t = useT()
  return (
    <section className="k-fpalarm" role="alert">
      <div className="k-fpalarm__head">
        <span className="k-fpalarm__glyph" aria-hidden>
          ⚠
        </span>
        <div>
          <h2 className="k-fpalarm__title">
            {t('host.fingerprintChanged')} — <span className="k-mono">{host.name}</span>
          </h2>
          <p className="k-fpalarm__hint">{t('host.fingerprintChangedHint')}</p>
        </div>
        <Badge tone="err">{t('host.fingerprintBlocked')}</Badge>
      </div>

      <dl className="k-fpalarm__prints">
        <div>
          <dt>{t('host.fingerprintExpected')}</dt>
          <dd>
            <span className="k-mono">{result.expectedFingerprint ?? t('state.unknown')}</span>
            {result.expectedFingerprint && (
              <CopyButton value={result.expectedFingerprint} ariaLabel={t('common.copy')} />
            )}
          </dd>
        </div>
        <div>
          <dt>{t('host.fingerprintObserved')}</dt>
          <dd>
            <span className="k-mono k-fpalarm__bad">{result.observedFingerprint ?? t('state.unknown')}</span>
            {result.observedFingerprint && (
              <CopyButton value={result.observedFingerprint} ariaLabel={t('common.copy')} />
            )}
          </dd>
        </div>
      </dl>
    </section>
  )
}

/* --------------------------------------------------------- Test report */

function HostTestReport({ result }: { result: HostTestResponse }) {
  const t = useT()
  return (
    <div className="k-testreport">
      <div className="k-testreport__head">
        <span className="k-label">{t('host.testResult')}</span>
        <span className="k-mono k-testreport__latency">{formatDuration(result.latencyMs)}</span>
      </div>
      <ul className="k-checks">
        {(result.checks ?? []).map((c) => {
          const state = c.skipped ? 'skip' : c.ok ? 'ok' : 'fail'
          return (
            <li key={c.name} className={`k-check k-check--${state}`}>
              <Icon
                icon={c.skipped ? MinusCircle : c.ok ? CheckCircle2 : XCircle}
                size={15}
                className="k-check__icon"
              />
              <span className="k-check__label">{checkLabel(c.name, t)}</span>
              {c.detail && <span className="k-check__detail">{c.detail}</span>}
            </li>
          )
        })}
      </ul>
      {/* First contact is not an incident, and must not be dressed as one: no
          key is pinned, so nothing was verified and nothing has changed. */}
      {result.fingerprintUnknown && (
        <Notice tone="warn">
          {t('host.fingerprintUnknown')}{' '}
          {result.observedFingerprint && <span className="k-mono">{result.observedFingerprint}</span>}
        </Notice>
      )}
    </div>
  )
}

/**
 * Whether this host's agent is the build the control plane is running.
 *
 * The version was already on screen and answered nothing: `defc409` means
 * nothing on its own, and an operator comparing seven of them against the
 * server's own build was doing by eye what this does. The comparison is exact
 * — both strings come from the same `git describe` — so "behind" is a fact
 * here, not an estimate.
 *
 * A connected agent that has reported no version at all is its own answer, and
 * it is not "current": it is unknown, and says so.
 */
/**
 * Whether restarting this agent would change how it behaves.
 *
 * It used to compare version strings — `host.nodeVersion !== health.version` —
 * and its own comment argued the comparison was exact because both came from
 * one `git describe`. Exact, and about the wrong thing: the control plane
 * redeploys constantly and the fleet does not, so every connected host went
 * amber minutes after every release. Amber on every row is the failure this
 * console has measured before; a signal that is everywhere is not a signal.
 *
 * The server now stamps a digest of the *node's own dependency closure* into
 * both binaries, the agent reports it, and `nodeContractState` is the comparison
 * — so a node built from a commit the control plane has moved past still reads
 * as current when nothing it runs has changed, and a node whose closure really
 * did move reads as behind however close the version strings look.
 *
 * Three states, never two. `UNMEASURED` is an agent old enough to predate the
 * stamp, and it is not a quieter kind of current: it has its own tone, because
 * unknown is not zero and not full.
 */
function AgentVersion({ host }: { host: HostResponse }) {
  const t = useT()
  if (host.nodeStatus !== 'CONNECTED') return null
  switch (host.nodeContractState) {
    case 'MATCHES':
      return (
        <Badge tone="ok" subtle>
          {t('host.agentCurrent')}
        </Badge>
      )
    case 'DIFFERS':
      return (
        <Badge tone="warn" subtle>
          {t('host.agentBehind')}
        </Badge>
      )
    default:
      // Absent as well as UNMEASURED: an older control plane, or a host record
      // written before the field existed, answers nothing rather than "no".
      return (
        <Badge tone="unknown" subtle>
          {t('host.agentVersionUnknown')}
        </Badge>
      )
  }
}

/* ----------------------------------------------------------- Host actions */

function HostActions({
  host,
  result,
  onResult,
}: {
  host: HostResponse
  result?: HostTestResponse
  onResult: (id: ID, result: HostTestResponse) => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const confirm = useConfirm()
  const navigate = useNavigate()
  // Three tones, not two. "Nothing needed doing" is not a success and it is
  // certainly not an error; rendering it in either colour tells the operator
  // something false about what just happened.
  const [message, setMessage] = useState<{ tone: 'ok' | 'err' | 'info'; text: string } | null>(
    null,
  )

  // A host mutation moves the live overview, the paginated list (setup wizard)
  // and the deep-link fallback all at once, so all three are refreshed.
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
    void queryClient.invalidateQueries({ queryKey: ['hosts'] })
    void queryClient.invalidateQueries({ queryKey: ['host', host.id] })
  }, [queryClient, host.id])

  const test = useMutation({
    // The verification runs inside the request and answers with what it found,
    // so the body is the result and is kept — that is where the host-key verdict
    // comes from.
    mutationFn: () => api.post<HostTestResponse>(`/hosts/${host.id}/test`),
    onSuccess: (outcome) => {
      onResult(host.id, outcome)
      setMessage({
        tone: outcome.reachable && !outcome.fingerprintChanged ? 'ok' : 'err',
        text: outcome.detail || outcome.checked,
      })
      invalidate()
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  const bootstrap = useMutation({
    mutationFn: () => api.post<unknown>(`/hosts/${host.id}/bootstrap`),
    onSuccess: () => {
      setMessage({ tone: 'ok', text: t('host.bootstrapStarted') })
      invalidate()
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  // Installing or updating a provider CLI. The server answers 202 and does the
  // work over SSH, reporting progress as events — which now reach this console
  // live, so the button's job ends at "started".
  const installCLI = useMutation({
    mutationFn: (prov: string) =>
      api.post<CLIInstallResponse>(`/hosts/${host.id}/cli/${prov}/install`),
    onSuccess: (res) => {
      // 200 with installing:false means the host already runs that version.
      // Reporting "started" there is a button that claims to have done
      // something; this says what is true.
      setMessage(
        res?.installing === false
          ? { tone: 'info', text: res.detail || t('host.cliAlreadyCurrent') }
          : { tone: 'ok', text: t('host.cliInstallStarted') },
      )
      invalidate()
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  // Offered per provider, and only where it would do something: a CLI that is
  // already current would be reinstalled for nothing, restarting it on a host
  // somebody may be working on.
  const cliActions = (host.clis ?? []).filter(
    (c) => c.freshness === 'BEHIND' || !c.installed,
  )

  // Removing the record, not the machine. The server refuses a host that still
  // holds account profiles and refuses the control host outright, so the button
  // is offered and the refusal explains itself rather than being pre-guessed
  // here — except for the control host, where hiding it is honest: there is no
  // state an operator could reach that would make it removable.
  const remove = useMutation({
    mutationFn: () => api.del<void>(`/hosts/${host.id}`),
    onSuccess: () => {
      invalidate()
      navigate('/hosts')
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  // An agent that is already connected can still be reinstalled, and that is
  // how it is upgraded: the same SSH install, which copies the current binary
  // and restarts the unit. The button used to be disabled the moment a host
  // reported CONNECTED, which left the console with no way to move an agent
  // forward at all — every upgrade had to be done by hand over SSH.
  const installed = host.nodeStatus === 'CONNECTED'

  // Provisioning takes a password for one connection, installs a generated key
  // and switches the host to key auth — the password is never stored (ADR-0011).
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [password, setPassword] = useState('')
  const provision = useMutation({
    mutationFn: () => api.post<HostResponse>(`/hosts/${host.id}/provision-key`, { password }),
    onSuccess: () => {
      setProvisionOpen(false)
      setPassword('')
      setMessage({ tone: 'ok', text: t('host.keyProvisioned') })
      invalidate()
      test.mutate()
    },
    onError: (err) => setMessage({ tone: 'err', text: describe(err).title }),
  })

  // Both actions open an SSH connection, so both are refused outright while the
  // key is in dispute. Disabling in the handler as well as in the markup keeps
  // the refusal true even if the button is reached another way.
  const blocked = result?.fingerprintChanged === true

  return (
    <div className="k-hostactions">
      {message && (
        <span
          className={`k-hostactions__msg k-hostactions__msg--${message.tone}`}
          role="status"
        >
          {message.text}
        </span>
      )}
      <Button
        size="sm"
        busy={test.isPending}
        disabled={blocked}
        title={blocked ? t('host.fingerprintBlocked') : undefined}
        onClick={() => {
          if (blocked) return
          test.mutate()
        }}
      >
        {t('host.testConnection')}
      </Button>
      <Button
        size="sm"
        busy={bootstrap.isPending}
        disabled={blocked}
        title={blocked ? t('host.fingerprintBlocked') : undefined}
        onClick={async () => {
          if (blocked) return
          // Reinstalling restarts the agent. Sessions survive it — the unit is
          // KillMode=process — but it is still a change to a working host, so
          // it is confirmed; a first install has nothing to interrupt.
          if (installed) {
            const ok = await confirm({
              title: t('host.upgradeAgent'),
              message: t('host.upgradeAgentConfirm', { host: host.name }),
              confirmLabel: t('host.upgradeAgent'),
              tone: 'default',
            })
            if (!ok) return
          }
          bootstrap.mutate()
        }}
      >
        {t(installed ? 'host.upgradeAgent' : 'host.bootstrap')}
      </Button>
      {/* One button per provider that would actually change something, named
          with the provider so a fleet running two CLIs is unambiguous. */}
      {!host.isControlHost &&
        cliActions.map((c) => (
          <Button
            key={c.provider}
            size="sm"
            busy={installCLI.isPending && installCLI.variables === c.provider}
            disabled={blocked || installCLI.isPending}
            onClick={() => installCLI.mutate(c.provider)}
          >
            {t(c.installed ? 'host.cliUpdateOne' : 'host.cliInstallOne', { cli: c.provider })}
          </Button>
        ))}
      {/* The control host has no SSH connection to provision a key over, so the
          action is hidden — Test and Bootstrap stay available. */}
      {!host.isControlHost && (
        <Button size="sm" variant="ghost" onClick={() => setProvisionOpen(true)}>
          <Icon icon={KeyRound} size={14} />
          {t('host.provisionKey')}
        </Button>
      )}
      {/* Last, and apart: it is the only act here that cannot be undone. Hidden
          on the control host, which the server refuses to remove — offering a
          button whose only outcome is a refusal is worse than not offering it. */}
      {!host.isControlHost && (
        <Button
          size="sm"
          variant="danger"
          busy={remove.isPending}
          onClick={async () => {
            const ok = await confirm({
              title: t('host.remove'),
              message: t('host.removeConfirm', { host: host.name }),
              confirmLabel: t('host.remove'),
            })
            if (ok) remove.mutate()
          }}
        >
          <Icon icon={Trash2} size={14} />
          {t('host.remove')}
        </Button>
      )}
      {provisionOpen && (
        <Dialog
          title={t('host.provisionKeyTitle')}
          onClose={() => setProvisionOpen(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setProvisionOpen(false)}>
                {t('action.cancel')}
              </Button>
              <Button
                variant="primary"
                busy={provision.isPending}
                disabled={!password.trim()}
                onClick={() => provision.mutate()}
              >
                {t('host.provisionKeySubmit')}
              </Button>
            </>
          }
        >
          <p className="k-dialog__lede">
            {t('host.provisionKeyHelp', { host: host.name, address: `${host.user}@${host.address}` })}
          </p>
          <TextField
            label={t('host.password')}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="off"
            name="host-provision-password"
            hint={t('host.provisionKeyHint')}
          />
        </Dialog>
      )}
    </div>
  )
}

/* --------------------------------------------------------- Add host dialog */

/** Exported so the first-run wizard adds a host through exactly the same form,
 *  with the same validation and the same key-based-access copy, as the pool
 *  screen does later. */
export function AddHostDialog({ onClose }: { onClose: () => void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()

  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [port, setPort] = useState('22')
  const [user, setUser] = useState('')
  const [labels, setLabels] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post<HostResponse>('/hosts', {
        name: name.trim(),
        address: address.trim(),
        port: Number(port) || 22,
        user: user.trim(),
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['overview'] })
      void queryClient.invalidateQueries({ queryKey: ['hosts'] })
      onClose()
    },
    onError: (err) => setError(describe(err).title),
  })

  const valid = name.trim() !== '' && address.trim() !== '' && user.trim() !== ''

  return (
    <Dialog
      title={t('host.add')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('action.cancel')}</Button>
          <Button variant="primary" busy={create.isPending} disabled={!valid} onClick={() => create.mutate()}>
            {t('action.create')}
          </Button>
        </>
      }
    >
      <Notice tone="info">{t('host.addHint')}</Notice>
      {error && <Notice tone="err">{error}</Notice>}
      <TextField label={t('common.name')} value={name} onChange={setName} required />
      <TextField label={t('host.address')} value={address} onChange={setAddress} required mono />
      <TextField label={t('host.port')} value={port} onChange={setPort} inputMode="numeric" mono />
      <TextField label={t('host.user')} value={user} onChange={setUser} required mono />
      <TextField
        label={t('host.labels')}
        value={labels}
        onChange={setLabels}
        placeholder={t('host.labelsPlaceholder')}
      />
    </Dialog>
  )
}
